import { useEffect, useRef } from 'react';
import { supabase } from '../../supabase';

const BURST_MS = 500;

/**
 * KAN-731 — live "your company's feature switches changed" signal.
 *
 * `public.feature_change_pings` (id, tenant_id UNIQUE, bumped_at) carries NO
 * data: CrewVex flips a switch (AI, plan, testing) and a trigger bumps the
 * tenant's single row. Members can SELECT their own tenant's row and the table
 * is in the realtime publication, so every open session learns of the change
 * within ~2s and silently re-reads `get_my_features` via `onPing`.
 *
 * Bursts dedupe: at most one `onPing` per BURST_MS, and a ping landing inside
 * the window schedules exactly one trailing call (the last event is never
 * dropped). Deliberately NOT subject to the 30s resume throttle — that stays
 * as the backup path only.
 */
export function useFeatureChangePing({ tenantId, enabled = true, onPing }) {
  const onPingRef = useRef(onPing);
  onPingRef.current = onPing;

  useEffect(() => {
    if (!enabled || !tenantId) return undefined;

    let lastRun = 0;
    let timer = null;
    const run = () => {
      lastRun = Date.now();
      onPingRef.current?.();
    };
    const ping = () => {
      const elapsed = Date.now() - lastRun;
      if (elapsed >= BURST_MS) { run(); return; }
      if (!timer) {
        timer = setTimeout(() => { timer = null; run(); }, BURST_MS - elapsed);
      }
    };

    const channel = supabase
      .channel(`feature-change-ping-${tenantId}`, { config: { private: true } })
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'feature_change_pings', filter: `tenant_id=eq.${tenantId}`,
      }, (payload) => {
        if (payload?.eventType === 'DELETE') return;
        ping();
      })
      .subscribe();

    return () => {
      if (timer) { clearTimeout(timer); timer = null; }
      supabase.removeChannel(channel);
    };
  }, [enabled, tenantId]);
}

export default useFeatureChangePing;
